import { useCustomerFields } from "@jugl-web/domain-resources/customers/hooks/useCustomerFields";
import { useTranslations, useSearchInput, cx } from "@jugl-web/utils";
import React, { useMemo, useCallback } from "react";
import { InteractiveContainer, Checkbox } from "@jugl-web/ui-components";
import { SearchInput } from "@jugl-web/ui-components/cross-platform/SearchInput";
import Highlighter from "react-highlight-words";
import { useCustomersContext } from "../../providers/useCustomersContext";

export const CustomerSearchQueryFiltersPopover: React.FC<{
  entityId: string;
}> = ({ entityId }) => {
  const { t } = useTranslations();
  const { queryFilters, setQueryFilters } = useCustomersContext();
  const { searchQueryFilters } = useCustomerFields({
    entityId,
  });
  const { searchQuery, inputProps } = useSearchInput();
  const filteredSearchQueryFilters = useMemo(
    () =>
      searchQuery
        ? searchQueryFilters?.filter(
            (searchQueryFilter) =>
              searchQueryFilter.title
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) &&
              searchQueryFilter.id !== "all"
          )
        : searchQueryFilters,
    [searchQueryFilters, searchQuery]
  );

  const onFilterSelect = useCallback(
    (filterId: string) => {
      const isFilterSelected = queryFilters.includes(filterId);

      if (filterId === "all") {
        if (searchQueryFilters.length - 2 === queryFilters.length) {
          setQueryFilters([]);
          return;
        }
        setQueryFilters(
          searchQueryFilters
            .filter((item) => item.id !== "all" && item.title !== "First Name")
            .map(({ id }) => id)
        );
        return;
      }
      if (isFilterSelected) {
        setQueryFilters(queryFilters.filter((id) => id !== filterId));
        return;
      }
      setQueryFilters([...queryFilters, filterId]);
    },
    [queryFilters, searchQueryFilters, setQueryFilters]
  );

  if (!searchQueryFilters) {
    return null;
  }

  return (
    <div className="flex w-[320px] flex-col gap-4 rounded-xl bg-white p-4 drop-shadow-[0_5px_16px_rgba(0,0,0,0.16)]">
      <span className="font-secondary text-sm leading-[140%] font-medium text-dark">
        {t({
          id: "customers-page.search-task-by",
          defaultMessage: "Search by",
        }).concat(":")}
      </span>
      <div className="flex flex-col gap-4">
        <SearchInput variant="filled" color="grey" {...inputProps} />
        <div
          className={cx(
            "jugl__custom-scrollbar flex max-h-[300px] flex-col gap-2 overflow-y-auto"
          )}
        >
          {filteredSearchQueryFilters?.length ? (
            filteredSearchQueryFilters.map((searchQueryFilter) => {
              const isFilterSelected = queryFilters.includes(
                searchQueryFilter.id
              );
              const isFilterDefault = searchQueryFilter.isDefault;
              const areAllFiltersSelected =
                queryFilters.length === searchQueryFilters.length - 2;
              const isAllFiltersOptionSelected =
                searchQueryFilter.id === "all" && areAllFiltersSelected;

              return (
                <InteractiveContainer
                  className={cx(
                    "flex items-center justify-between gap-3 rounded-lg p-3 transition-colors hover:bg-grey-100",
                    (isFilterSelected ||
                      isFilterDefault ||
                      isAllFiltersOptionSelected) &&
                      "bg-[#EFF8FF] hover:bg-[#EFF8FF]"
                  )}
                  key={searchQueryFilter.id}
                  onClick={() => {
                    if (isFilterDefault) return;
                    onFilterSelect(searchQueryFilter.id);
                  }}
                  isDisabled={searchQueryFilter.id === "name"}
                >
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div
                      className={cx(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                        searchQueryFilter.backgroundColor
                      )}
                    >
                      {searchQueryFilter.icon}
                    </div>
                    <span
                      className={cx(
                        "truncate font-secondary text-sm leading-[140%] text-dark",
                        searchQueryFilter.id === "all" && "font-medium"
                      )}
                    >
                      <Highlighter
                        autoEscape
                        searchWords={[searchQuery]}
                        textToHighlight={searchQueryFilter.title}
                        highlightClassName="text-primary font-semibold bg-transparent"
                      />
                      {searchQueryFilter.id === "all" && (
                        <span className="font-secondary text-sm leading-[140%] text-grey-800">
                          {" "}
                          {searchQueryFilters.length - 1}
                        </span>
                      )}
                    </span>
                  </div>
                  {isFilterDefault ? (
                    <span className="font-secondary text-xs leading-[140%] text-grey-800">
                      {t({
                        id: "tasks-page.default",
                        defaultMessage: "Default",
                      })}
                    </span>
                  ) : (
                    <Checkbox
                      readOnly
                      isChecked={
                        isFilterSelected || Boolean(isAllFiltersOptionSelected)
                      }
                    />
                  )}
                </InteractiveContainer>
              );
            })
          ) : (
            <span className="text-center font-secondary text-sm leading-[160%] text-[#828282]">
              {t({
                id: "tasks-page.no-results",
                defaultMessage: "No Results 😔",
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
